import React, { useState, useEffect, useMemo } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ImageUpload, { ImageFile, PRODUCT_IMAGE_LIMIT } from '../components/ImageUpload';
import ImageViewer from '../components/ImageViewer';
import { useTranslations } from '../translations';
import { resolveUploadErrorMessage } from '../utils/uploadError';
import { searchUserLocations, searchLocations } from '../services/location.api';
import { type Location } from '../services/api.types';
import { fetchCategories } from '../services/category.api';
import { fetchOwnProductDetail, updateProduct } from '../services/product.api';
import { getAccessToken, getUserId } from '../services/auth';
import BackButton from '../components/BackButton';
import LoadingIndicator from '../components/LoadingIndicator';
import {
  PRODUCT_DESCRIPTION_MAX_LENGTH,
  PRODUCT_TITLE_MAX_LENGTH,
} from '../constants/product';
import { isProductPriceInputAllowed, parseProductPrice } from '../utils/productPrice';
import { useNotification } from '../components/NotificationProvider';
import { fetchUserProfile } from '../services/user.api';
import ProductPreviewModal from '../components/ProductPreviewModal';
import UnitTypeSelect from '../components/UnitTypeSelect';
import { normalizeUnitType } from '../utils/unitType';
import {
  formatLocationDisplay,
  haveSameLocationIds,
  LOCATION_SEARCH_PAGE_SIZE,
  locationMatchesQuery,
  mergeUniqueLocations
} from '../utils/locationDisplay';
import './CreateProduct.css';
import '../components/ThemedDropdown.css';

interface EditProductProps {
  isLoggedIn: boolean;
  isAuthChecking: boolean;
  productId: string;
  onNavigateToLogin: () => void;
  onNavigateToRegister: () => void;
  onNavigateToHome: () => void;
  onLogout: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToMyAnnouncements?: (productType?: 'PRODUCT' | 'DEMAND') => void;
  onNavigateToLocations?: () => void;
  onNavigateToMessages?: (productId?: string | null) => void;
  onNavigateToCreateProduct?: () => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToCookies?: () => void;
  onNavigateToAbout?: () => void;
  onNavigateToContact?: () => void;
}

const EditProduct: React.FC<EditProductProps> = ({
  isLoggedIn,
  isAuthChecking,
  productId,
  onNavigateToLogin,
  onNavigateToRegister,
  onNavigateToHome,
  onLogout,
  onNavigateToProfile,
  onNavigateToMyAnnouncements,
  onNavigateToLocations,
  onNavigateToMessages,
  onNavigateToCreateProduct,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookies,
  onNavigateToAbout,
  onNavigateToContact
}) => {
  const t = useTranslations();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [productType, setProductType] = useState<'SELL' | 'BUY'>('SELL');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [priceUnit, setPriceUnit] = useState('');
  const [negotiable, setNegotiable] = useState(false);
  const [location, setLocation] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [locationSearch, setLocationSearch] = useState('');
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [locationOptions, setLocationOptions] = useState<Location[]>([]);
  const [userLocations, setUserLocations] = useState<Location[]>([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationLoadingMore, setLocationLoadingMore] = useState(false);
  const [locationPage, setLocationPage] = useState(1);
  const [locationTotalPages, setLocationTotalPages] = useState(1);
  const [category, setCategory] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [categorySearch, setCategorySearch] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [subcategory, setSubcategory] = useState('');
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string>('');
  const [subcategorySearch, setSubcategorySearch] = useState('');
  const [showSubcategoryDropdown, setShowSubcategoryDropdown] = useState(false);
  const [subcategoryOptions, setSubcategoryOptions] = useState<any[]>([]);
  const [allSubcategories, setAllSubcategories] = useState<any[]>([]);
  const [images, setImages] = useState<ImageFile[]>([]);
  const [isPublishing, setIsPublishing] = useState(false);
  const [imagesProcessing, setImagesProcessing] = useState(false);
  const [hasAttemptedPublish, setHasAttemptedPublish] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerInitialIndex, setViewerInitialIndex] = useState(0);

  const titleInvalid = hasAttemptedPublish && !title.trim();
  const descriptionInvalid = hasAttemptedPublish && !description.trim();
  const priceValue = parseProductPrice(price);
  const priceInvalid = hasAttemptedPublish && priceValue === null;
  const priceUnitInvalid = hasAttemptedPublish && !priceUnit.trim();
  const locationInvalid = hasAttemptedPublish && !selectedLocationId;
  const categoryInvalid = hasAttemptedPublish && !selectedCategoryId;

  // Store original values for change detection
  const [originalData, setOriginalData] = useState({
    productType: 'SELL' as 'SELL' | 'BUY',
    title: '',
    description: '',
    price: '',
    priceUnit: '',
    negotiable: false,
    selectedLocationId: '',
    selectedCategoryId: '',
    selectedSubcategoryId: '',
    images: [] as ImageFile[]
  });

  // Check if any data has changed from original
  const hasChanges = useMemo(() => {
    // Check basic fields
    if (productType !== originalData.productType) return true;
    if (title !== originalData.title) return true;
    if (description !== originalData.description) return true;
    if (price !== originalData.price) return true;
    if (priceUnit !== originalData.priceUnit) return true;
    if (negotiable !== originalData.negotiable) return true;
    if (selectedLocationId !== originalData.selectedLocationId) return true;
    if (selectedCategoryId !== originalData.selectedCategoryId) return true;
    if (selectedSubcategoryId !== originalData.selectedSubcategoryId) return true;

    // Check if images changed
    if (images.length !== originalData.images.length) return true;

    // Check if image order or content changed
    for (let i = 0; i < images.length; i++) {
      if (images[i].preview !== originalData.images[i]?.preview) return true;
      if (images[i].isExisting !== originalData.images[i]?.isExisting) return true;
    }

    return false;
  }, [
    productType, title, description, price, priceUnit, negotiable,
    selectedLocationId, selectedCategoryId, selectedSubcategoryId,
    images, originalData
  ]);

  useEffect(() => {
    const loadProductData = async () => {
      try {
        setLoading(true);
        const productDetail = await fetchOwnProductDetail(productId);

        setTitle(productDetail.title);
        setDescription(productDetail.description);
        setPrice(productDetail.price.price.toString());
        const normalizedPriceUnit = normalizeUnitType(productDetail.price.unitType);
        setPriceUnit(normalizedPriceUnit);
        setNegotiable(productDetail.negotiable);
        setLocation(formatLocationDisplay(productDetail.locationDto));
        setSelectedLocationId(productDetail.locationDto.id);
        setCategory(productDetail.category.name);
        setSelectedCategoryId(productDetail.category.id);

        // Fetch full category data to get subcategories
        try {
          const categoriesResponse = await fetchCategories();
          const fullCategory = categoriesResponse.content.find(cat => cat.id === productDetail.category.id);
          if (fullCategory) {
            setAllSubcategories(fullCategory.subCategories || []);
            setSubcategoryOptions(fullCategory.subCategories || []);
          }
        } catch (categoryError) {
          console.error('Error loading category subcategories:', categoryError);
          // Fallback to product detail subcategories if available
          setAllSubcategories(productDetail.category.subCategories || []);
          setSubcategoryOptions(productDetail.category.subCategories || []);
        }

        if (productDetail.subCategory) {
          setSubcategory(productDetail.subCategory.name);
          setSelectedSubcategoryId(productDetail.subCategory.id);
        }

        setProductType(productDetail.productType === 'PRODUCT' ? 'SELL' : 'BUY');

        const imageFiles: ImageFile[] = productDetail.images.map((img, index) => ({
          id: `existing-${index}`,
          preview: img.imageUrl,
          file: null,
          isExisting: true,
          imageId: img.id
        }));
        setImages(imageFiles);

        // Store original data for change detection
        setOriginalData({
          productType: productDetail.productType === 'PRODUCT' ? 'SELL' : 'BUY',
          title: productDetail.title,
          description: productDetail.description,
          price: productDetail.price.price.toString(),
          priceUnit: normalizedPriceUnit,
          negotiable: productDetail.negotiable,
          selectedLocationId: productDetail.locationDto.id,
          selectedCategoryId: productDetail.category.id,
          selectedSubcategoryId: productDetail.subCategory?.id || '',
          images: imageFiles
        });

      } catch (error) {
        console.error('Error loading product:', error);
        const errorMessage = error instanceof Error ? error.message : '';
        if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
          onNavigateToLogin();
          return;
        }
        alert('Nu am putut încărca datele produsului. Te rugăm să încerci din nou.');
        window.history.back();
      } finally {
        setLoading(false);
      }
    };

    loadProductData();
  }, [productId, onNavigateToLogin]);


  const handlePreview = async () => {
    setHasAttemptedPublish(true);

    if (!title.trim() || !description.trim() || !price.trim() || !priceUnit.trim() || !selectedLocationId || !selectedCategoryId) {
      showNotification(t.createProduct.errorMissingFields, 'error');
      return;
    }

    const priceValue = parseProductPrice(price);
    if (priceValue === null) {
      showNotification(t.createProduct.errorInvalidPrice, 'error');
      return;
    }

    try {
      const userProfile = await fetchUserProfile();
      let selectedLoc = locationOptions.find(loc => loc.id === selectedLocationId);

      if (!selectedLoc) {
        selectedLoc = userLocations.find(loc => loc.id === selectedLocationId);
      }

      if (!selectedLoc && selectedLocationId) {
        try {
          const response = await searchUserLocations(getUserId()!, '', 1, 100);
          selectedLoc = response.content.find(loc => loc.id === selectedLocationId);
        } catch (e) {
          console.error('Error fetching location for preview:', e);
        }
      }

      const previewData = {
        productType,
        title,
        description,
        price: {
          price: priceValue,
          currency: 'RON',
          unitType: priceUnit
        },
        images: images.map(img => ({ preview: img.preview })),
        categoryName: category,
        subCategoryName: subcategory || undefined,
        locationName: selectedLoc?.diacriticsName || location.split(',')[0].trim(),
        countyName: selectedLoc?.county.name || location.split(',')[1]?.trim() || '',
        coordinates: selectedLoc?.coordinates,
        negotiable,
        userFirstName: userProfile.firstName,
        userLastName: userProfile.lastName,
        userImageUrl: userProfile.imageUrl,
        userPhone: userProfile.phone
      };

      console.log('Preview data coordinates:', previewData.coordinates);
      setPreviewData(previewData);
      setShowPreview(true);
    } catch (error) {
      console.error('Error building preview:', error);
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      showNotification('Nu s-au putut încărca datele pentru previzualizare', 'error');
    }
  };

  useEffect(() => {
    const searchLocationsByName = async () => {
      if (!showLocationDropdown) {
        return;
      }

      const searchTerm = locationSearch.trim();

      if (!searchTerm && userLocations.length > 0) {
        setLocationOptions(userLocations);
      }

      const accessToken = getAccessToken();
      const userId = getUserId();

      if (!accessToken || !userId) {
        setLocationOptions([]);
        return;
      }

      setLocationLoading(true);

      try {
        const userLocationsResponse = await searchUserLocations(
          userId,
          searchTerm,
          1,
          LOCATION_SEARCH_PAGE_SIZE
        );
        const savedLocationMatches = mergeUniqueLocations(
          userLocations.filter(loc => locationMatchesQuery(loc, searchTerm)),
          userLocationsResponse.content
        );

        if (!searchTerm && userLocationsResponse.content.length > 0) {
          setUserLocations(prev => (
            haveSameLocationIds(prev, userLocationsResponse.content) ? prev : userLocationsResponse.content
          ));
        } else if (searchTerm && userLocationsResponse.content.length > 0) {
          setUserLocations(prev => {
            const mergedLocations = mergeUniqueLocations(prev, userLocationsResponse.content);
            return haveSameLocationIds(prev, mergedLocations) ? prev : mergedLocations;
          });
        }

        const locationsResponse = await searchLocations(
          1,
          LOCATION_SEARCH_PAGE_SIZE,
          null,
          searchTerm || null,
          accessToken
        );
        const publicLocationsPage = locationsResponse.locations;

        setLocationOptions(mergeUniqueLocations(savedLocationMatches, publicLocationsPage.content));
        setLocationPage(publicLocationsPage.pageNumber || 1);
        setLocationTotalPages(publicLocationsPage.totalPages || 1);
      } catch (error) {
        console.error('Error searching locations:', error);
        const errorMessage = error instanceof Error ? error.message : '';
        if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
          onNavigateToLogin();
          return;
        }
        setLocationOptions([]);
      } finally {
        setLocationLoading(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      searchLocationsByName();
    }, locationSearch.trim() ? 300 : 0);

    return () => clearTimeout(debounceTimer);
  }, [locationSearch, showLocationDropdown, onNavigateToLogin]);

  useEffect(() => {
    if (!showCategoryDropdown || categorySearch.length === 0) {
      return;
    }

    const filterCategories = async () => {
      try {
        const response = await fetchCategories();
        const searchLower = categorySearch.toLowerCase();
        const filtered = response.content.filter(cat =>
          cat.name.toLowerCase().includes(searchLower)
        );
        setCategoryOptions(filtered);
      } catch (error) {
        console.error('Error filtering categories:', error);
      }
    };

    const debounceTimer = setTimeout(() => {
      filterCategories();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [categorySearch, showCategoryDropdown]);

  useEffect(() => {
    if (!showSubcategoryDropdown) {
      return;
    }

    if (subcategorySearch.length === 0) {
      setSubcategoryOptions(allSubcategories);
      return;
    }

    const searchLower = subcategorySearch.toLowerCase();
    const filtered = allSubcategories.filter(subcat =>
      subcat.name.toLowerCase().includes(searchLower)
    );
    setSubcategoryOptions(filtered);
  }, [subcategorySearch, showSubcategoryDropdown, allSubcategories]);

  const handleLocationFocus = () => {
    setLocationOptions(userLocations);
    setLocationPage(1);
    setLocationTotalPages(1);
    setLocationSearch('');
    setShowLocationDropdown(true);
  };

  const handleLocationSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocationOptions([]);
    setLocationPage(1);
    setLocationTotalPages(1);
    setLocationSearch(value);
    setLocation(value);
    setSelectedLocationId('');
    setShowLocationDropdown(true);
  };

  const handleLocationSelect = (loc: Location) => {
    setLocation(formatLocationDisplay(loc));
    setSelectedLocationId(loc.id);
    setShowLocationDropdown(false);
    setLocationSearch('');
  };

  const loadMoreLocationOptions = async () => {
    if (locationLoading || locationLoadingMore || locationPage >= locationTotalPages) {
      return;
    }

    const accessToken = getAccessToken();
    const userId = getUserId();

    if (!accessToken || !userId) {
      return;
    }

    const nextPage = locationPage + 1;
    const searchTerm = locationSearch.trim();
    setLocationLoadingMore(true);

    try {
      const locationsResponse = await searchLocations(
        nextPage,
        LOCATION_SEARCH_PAGE_SIZE,
        null,
        searchTerm || null,
        accessToken
      );
      const publicLocationsPage = locationsResponse.locations;

      setLocationOptions(prev => mergeUniqueLocations(prev, publicLocationsPage.content));
      setLocationPage(publicLocationsPage.pageNumber || nextPage);
      setLocationTotalPages(publicLocationsPage.totalPages || locationTotalPages);
    } catch (error) {
      console.error('Error loading more locations:', error);
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
      }
    } finally {
      setLocationLoadingMore(false);
    }
  };

  const handleLocationDropdownScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - scrollTop - clientHeight <= 24) {
      loadMoreLocationOptions();
    }
  };

  const userLocationIds = new Set(userLocations.map(loc => loc.id));
  const userLocationOptions = locationOptions.filter(loc => userLocationIds.has(loc.id));
  const allLocationOptions = locationOptions.filter(loc => !userLocationIds.has(loc.id));

  const handleCategoryFocus = async () => {
    try {
      const response = await fetchCategories();
      setCategoryOptions(response.content);
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategoryOptions([]);
    }

    setCategorySearch('');
    setShowCategoryDropdown(true);
  };

  const handleCategorySelect = (cat: any) => {
    setCategory(cat.name);
    setSelectedCategoryId(cat.id);
    const subcats = cat.subCategories || [];
    setAllSubcategories(subcats);
    setSubcategoryOptions(subcats);
    setSubcategory('');
    setSelectedSubcategoryId('');
    setShowCategoryDropdown(false);
    setCategorySearch('');
  };

  const handleSubcategorySelect = (subcat: any) => {
    setSubcategory(subcat.name);
    setSelectedSubcategoryId(subcat.id);
    setShowSubcategoryDropdown(false);
    setSubcategorySearch('');
  };

  const handleImagesChange = (newImages: ImageFile[]) => {
    setImages(newImages);
  };

  const handlePublish = async () => {
    setHasAttemptedPublish(true);

    if (!title.trim() || !description.trim() || !price.trim() || !priceUnit.trim() || !selectedLocationId || !selectedCategoryId) {
      showNotification(t.createProduct.errorMissingFields, 'error');
      return;
    }

    const accessToken = getAccessToken();
    if (!accessToken) {
      showNotification(t.login.loginError, 'error');
      return;
    }

    setIsPublishing(true);

    try {
      if (priceValue === null) {
        showNotification(t.createProduct.errorInvalidPrice, 'error');
        setIsPublishing(false);
        return;
      }

      const updateData = {
        title,
        description,
        price: {
          price: priceValue,
          currency: 'RON',
          quantity: 1,
          unitType: priceUnit
        },
        categoryId: selectedCategoryId,
        ...(selectedSubcategoryId && { subCategoryId: selectedSubcategoryId }),
        negotiable,
        locationId: selectedLocationId,
        productType: productType === 'SELL' ? 'PRODUCT' as const : 'DEMAND' as const
      };

      const existingImages = images.filter(img => img.isExisting);
      const newImages = images.filter(img => !img.isExisting && img.file);
      const retainedImageIds = existingImages
        .map(img => img.imageId)
        .filter((imageId): imageId is string => Boolean(imageId));
      const favoriteImage = images[0];
      const favoriteExistingImageId = favoriteImage?.isExisting
        ? favoriteImage.imageId || null
        : null;
      const favoriteNewImageIndex = favoriteImage && !favoriteImage.isExisting
        ? newImages.findIndex(img => img.id === favoriteImage.id)
        : null;

      await updateProduct(
        productId,
        updateData,
        {
          retainedImageIds,
          favoriteExistingImageId,
          favoriteNewImageIndex
        },
        newImages.map(img => img.file!)
      );

      showNotification(t.editProduct.successUpdated, 'success');
      showNotification(t.editProduct.verificationPending, 'info');
      onNavigateToMyAnnouncements?.(updateData.productType);
    } catch (error) {
      console.error('Error updating product:', error);
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      const uploadError = resolveUploadErrorMessage(error, t.upload);
      if (uploadError) {
        showNotification(uploadError, 'error');
        return;
      }
      showNotification('Eroare la actualizarea produsului', 'error');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCancel = () => {
    if (onNavigateToMyAnnouncements) {
      onNavigateToMyAnnouncements(productType === 'SELL' ? 'PRODUCT' : 'DEMAND');
      return;
    }
    onNavigateToHome();
  };

  if (loading) {
    return (
      <>
      {showPreview && previewData && (
        <ProductPreviewModal
          previewData={previewData}
          onClose={() => setShowPreview(false)}
        />
      )}
      {isViewerOpen && images.length > 0 && (
        <ImageViewer
          images={images.map(img => img.preview)}
          initialIndex={viewerInitialIndex}
          onClose={() => setIsViewerOpen(false)}
        />
      )}
      <div className="create-or-update-page">
        <Header
          isLoggedIn={isLoggedIn}
          onNavigateToLogin={onNavigateToLogin}
          onNavigateToRegister={onNavigateToRegister}
          onNavigateToHome={onNavigateToHome}
          onLogout={onLogout}
          onProfileClick={onNavigateToProfile}
          onLocationsClick={onNavigateToLocations}
          onMessagesClick={onNavigateToMessages}
          onAnnouncementsClick={onNavigateToMyAnnouncements}
          onNavigateToCreateProduct={onNavigateToCreateProduct}
        />
        <main className="create-or-update-content">
          <LoadingIndicator message={t.productDetail.loading} variant="page" />
        </main>
      </div>
      </>
    );
  }

  return (
    <>
      {showPreview && previewData && (
        <ProductPreviewModal
          previewData={previewData}
          onClose={() => setShowPreview(false)}
        />
      )}
      {isViewerOpen && images.length > 0 && (
        <ImageViewer
          images={images.map(img => img.preview)}
          initialIndex={viewerInitialIndex}
          onClose={() => setIsViewerOpen(false)}
        />
      )}
      <div className="create-or-update-page">
      <Header
        isLoggedIn={isLoggedIn}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={onNavigateToRegister}
        onNavigateToHome={onNavigateToHome}
        onLogout={onLogout}
        onProfileClick={onNavigateToProfile}
        onLocationsClick={onNavigateToLocations}
        onMessagesClick={onNavigateToMessages}
        onAnnouncementsClick={onNavigateToMyAnnouncements}
        onNavigateToCreateProduct={onNavigateToCreateProduct}
      />

      <main className="create-or-update-content">
        <BackButton className="create-or-update-back-button" onClick={() => window.history.back()} />

        <h1 className="create-or-update-page-title">
          Modifică anunț
        </h1>

        <div className="create-or-update-type-selector">
          <button
            type="button"
            className={`create-or-update-type-option ${productType === 'SELL' ? 'selected' : ''}`}
            aria-pressed={productType === 'SELL'}
            onClick={() => setProductType('SELL')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              {productType === 'SELL' ? (
                <>
                  <mask id="path-1-inside-1" fill="white">
                    <path d="M12 0C5.3724 0 0 5.3736 0 12C0 18.6264 5.3724 24 12 24C18.6276 24 24 18.6264 24 12C24 5.3736 18.6276 0 12 0Z"/>
                  </mask>
                  <path d="M12 0C5.3724 0 0 5.3736 0 12C0 18.6264 5.3724 24 12 24C18.6276 24 24 18.6264 24 12C24 5.3736 18.6276 0 12 0Z" fill="#F5FAF2"/>
                  <path d="M12 -2C4.2677 -2 -2 4.26916 -2 12H2C2 6.47804 6.4771 2 12 2V-2ZM-2 12C-2 19.7308 4.2677 26 12 26V22C6.4771 22 2 17.522 2 12H-2ZM12 26C19.7323 26 26 19.7308 26 12H22C22 17.522 17.5229 22 12 22V26ZM26 12C26 4.26916 19.7323 -2 12 -2V2C17.5229 2 22 6.47804 22 12H26Z" fill="#267200" mask="url(#path-1-inside-1)"/>
                  <path d="M15.2455 7.67351L10.7047 13.6363L8.74509 11.1331C8.33709 10.6099 7.58109 10.5187 7.06029 10.9279C6.53829 11.3359 6.44589 12.0907 6.85509 12.6115L9.77229 16.3399C10.0003 16.6303 10.3483 16.7995 10.7179 16.7995H10.7263C11.0971 16.7983 11.4475 16.6231 11.6719 16.3267L17.1547 9.12671C17.5567 8.59871 17.4547 7.84751 16.9267 7.44551C16.3975 7.04351 15.6463 7.14671 15.2455 7.67351Z" fill="#267200"/>
                </>
              ) : (
                <path fillRule="evenodd" clipRule="evenodd" d="M0 12C0 5.3736 5.3724 0 12 0C18.6276 0 24 5.3736 24 12C24 18.6264 18.6276 24 12 24C5.3724 24 0 18.6264 0 12ZM2.4 12C2.4 17.2932 6.7068 21.6 12 21.6C17.2932 21.6 21.6 17.2932 21.6 12C21.6 6.7068 17.2932 2.4 12 2.4C6.7068 2.4 2.4 6.7068 2.4 12Z" fill="#071105"/>
              )}
            </svg>
            <span>{t.createProduct.wantToSell}</span>
          </button>
          <button
            type="button"
            className={`create-or-update-type-option ${productType === 'BUY' ? 'selected' : ''}`}
            aria-pressed={productType === 'BUY'}
            onClick={() => setProductType('BUY')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              {productType === 'BUY' ? (
                <>
                  <mask id="path-1-inside-2" fill="white">
                    <path d="M12 0C5.3724 0 0 5.3736 0 12C0 18.6264 5.3724 24 12 24C18.6276 24 24 18.6264 24 12C24 5.3736 18.6276 0 12 0Z"/>
                  </mask>
                  <path d="M12 0C5.3724 0 0 5.3736 0 12C0 18.6264 5.3724 24 12 24C18.6276 24 24 18.6264 24 12C24 5.3736 18.6276 0 12 0Z" fill="#F5FAF2"/>
                  <path d="M12 -2C4.2677 -2 -2 4.26916 -2 12H2C2 6.47804 6.4771 2 12 2V-2ZM-2 12C-2 19.7308 4.2677 26 12 26V22C6.4771 22 2 17.522 2 12H-2ZM12 26C19.7323 26 26 19.7308 26 12H22C22 17.522 17.5229 22 12 22V26ZM26 12C26 4.26916 19.7323 -2 12 -2V2C17.5229 2 22 6.47804 22 12H26Z" fill="#267200" mask="url(#path-1-inside-2)"/>
                  <path d="M15.2455 7.67351L10.7047 13.6363L8.74509 11.1331C8.33709 10.6099 7.58109 10.5187 7.06029 10.9279C6.53829 11.3359 6.44589 12.0907 6.85509 12.6115L9.77229 16.3399C10.0003 16.6303 10.3483 16.7995 10.7179 16.7995H10.7263C11.0971 16.7983 11.4475 16.6231 11.6719 16.3267L17.1547 9.12671C17.5567 8.59871 17.4547 7.84751 16.9267 7.44551C16.3975 7.04351 15.6463 7.14671 15.2455 7.67351Z" fill="#267200"/>
                </>
              ) : (
                <path fillRule="evenodd" clipRule="evenodd" d="M0 12C0 5.3736 5.3724 0 12 0C18.6276 0 24 5.3736 24 12C24 18.6264 18.6276 24 12 24C5.3724 24 0 18.6264 0 12ZM2.4 12C2.4 17.2932 6.7068 21.6 12 21.6C17.2932 21.6 21.6 17.2932 21.6 12C21.6 6.7068 17.2932 2.4 12 2.4C6.7068 2.4 2.4 6.7068 2.4 12Z" fill="#071105"/>
              )}
            </svg>
            <span>{t.createProduct.wantToBuy}</span>
          </button>
        </div>

        <section className="create-or-update-images-section">
          <ImageUpload
            images={images}
            onImagesChange={handleImagesChange}
            maxImages={PRODUCT_IMAGE_LIMIT}
            hint={t.createProduct.imagesHint}
            validationMessages={t.upload}
            onProcessingChange={setImagesProcessing}
            onImageClick={(index) => {
              setViewerInitialIndex(index);
              setIsViewerOpen(true);
            }}
          />
        </section>

        <section className="create-or-update-details-section">
          <div className="create-or-update-section-header">
            <h2 className="create-or-update-section-title">{t.createProduct.detailsTitle}</h2>
          </div>
          <div className="create-or-update-details-section-wrapper">
            <div className="create-or-update-title-location-section">
              <div className="create-or-update-title-field-group">
                <div className="create-or-update-field-label-row">
                  <label className="create-or-update-field-label">
                    Titlu
                    <span className="create-or-update-mandatory-indicator">*</span>
                  </label>
                  <span className="create-or-update-char-counter">
                    {title.length}/{PRODUCT_TITLE_MAX_LENGTH}
                  </span>
                </div>
                <input
                  type="text"
                  className={`create-or-update-text-input ${titleInvalid ? 'create-or-update-field-invalid' : ''}`}
                  placeholder={t.createProduct.titlePlaceholder}
                  value={title}
                  onChange={(e) => setTitle(e.target.value.slice(0, PRODUCT_TITLE_MAX_LENGTH))}
                  maxLength={PRODUCT_TITLE_MAX_LENGTH}
                  required
                  aria-invalid={titleInvalid}
                />
              </div>
              <div className="create-or-update-location-field-group">
                <label className="create-or-update-field-label">
                  {t.createProduct.locationLabel}
                  <span className="create-or-update-mandatory-indicator">*</span>
                </label>
                <div className="create-or-update-location-dropdown-wrapper">
                  <input
                    type="text"
                    className={`create-or-update-text-input create-or-update-location-dropdown-input ${locationInvalid ? 'create-or-update-field-invalid' : ''}`}
                    placeholder={t.createProduct.locationPlaceholder}
                    value={showLocationDropdown ? locationSearch : location}
                    onChange={handleLocationSearchChange}
                    onFocus={handleLocationFocus}
                    onBlur={() => {
                      setTimeout(() => {
                        setShowLocationDropdown(false);
                        setLocationSearch('');
                      }, 200);
                    }}
                    required
                    aria-invalid={locationInvalid}
                  />
                  <svg className={`create-or-update-location-chevron themed-dropdown-chevron ${showLocationDropdown ? 'themed-dropdown-chevron-open' : ''}`} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.9999 16C11.7719 16 11.5449 15.923 11.3599 15.768L5.35991 10.768C4.93591 10.415 4.87791 9.78399 5.23191 9.35999C5.58491 8.93599 6.21491 8.87899 6.63991 9.23199L12.0109 13.708L17.3729 9.39299C17.8029 9.04699 18.4329 9.11499 18.7789 9.54499C19.1249 9.97499 19.0569 10.604 18.6269 10.951L12.6269 15.779C12.4439 15.926 12.2219 16 11.9999 16Z" fill="#071105"/>
                  </svg>
                  {showLocationDropdown && (
                    <div className="create-or-update-location-dropdown-options themed-dropdown-menu" onScroll={handleLocationDropdownScroll}>
                      {locationLoading && locationOptions.length === 0 ? (
                        <div className="create-or-update-location-option-empty">{t.common.loading}</div>
                      ) : locationOptions.length > 0 ? (
                        <>
                          {userLocationOptions.length > 0 && (
                            <>
                              <div className="create-or-update-location-group-label">{t.createProduct.yourLocations}</div>
                              {userLocationOptions.map((loc) => (
                                <div
                                  key={loc.id}
                                  className={`create-or-update-location-option themed-dropdown-option ${selectedLocationId === loc.id ? 'themed-dropdown-option-active' : ''}`}
                                  onClick={() => handleLocationSelect(loc)}
                                >
                                  <div className="create-or-update-location-option-name">{loc.diacriticsName}</div>
                                  <div className="create-or-update-location-option-county">{loc.county.name}</div>
                                </div>
                              ))}
                            </>
                          )}
                          {allLocationOptions.length > 0 && (
                            <>
                              {userLocationOptions.length > 0 && <div className="create-or-update-location-group-divider" />}
                              <div className="create-or-update-location-group-label">{t.createProduct.allLocations}</div>
                              {allLocationOptions.map((loc) => (
                                <div
                                  key={loc.id}
                                  className={`create-or-update-location-option themed-dropdown-option ${selectedLocationId === loc.id ? 'themed-dropdown-option-active' : ''}`}
                                  onClick={() => handleLocationSelect(loc)}
                                >
                                  <div className="create-or-update-location-option-name">{loc.diacriticsName}</div>
                                  <div className="create-or-update-location-option-county">{loc.county.name}</div>
                                </div>
                              ))}
                            </>
                          )}
                          {(locationLoading || locationLoadingMore) && (
                            <div className="create-or-update-location-option-empty">{t.common.loading}</div>
                          )}
                        </>
                      ) : (
                        <div className="create-or-update-location-option-empty">{t.common.noResults}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="create-or-update-description-field-group">
              <div className="create-or-update-field-label-row">
                <label className="create-or-update-field-label">
                  {t.createProduct.descriptionLabel}
                  <span className="create-or-update-mandatory-indicator">*</span>
                </label>
                <span className="create-or-update-char-counter">
                  {description.length}/{PRODUCT_DESCRIPTION_MAX_LENGTH}
                </span>
              </div>
              <textarea
                className={`create-or-update-textarea-input ${descriptionInvalid ? 'create-or-update-field-invalid' : ''}`}
                placeholder={t.createProduct.descriptionPlaceholder}
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, 500))}
                maxLength={PRODUCT_DESCRIPTION_MAX_LENGTH}
                rows={4}
                required
                aria-invalid={descriptionInvalid}
              />
            </div>

            <div className="create-or-update-price-section">
              <div className="create-or-update-price-field-group">
                <label className="create-or-update-field-label">
                  {t.createProduct.priceLabel}
                  <span className="create-or-update-mandatory-indicator">*</span>
                </label>
                <input
                  type="text"
                  className={`create-or-update-text-input ${priceInvalid ? 'create-or-update-field-invalid' : ''}`}
                  placeholder={t.createProduct.pricePlaceholder}
                  value={price}
                  inputMode="decimal"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (isProductPriceInputAllowed(value)) {
                      setPrice(value);
                    }
                  }}
                  required
                  aria-invalid={priceInvalid}
                />
              </div>

              <div className="create-or-update-price-unit-field-group">
                <label className="create-or-update-field-label">
                  {t.createProduct.priceUnitLabel}
                  <span className="create-or-update-mandatory-indicator">*</span>
                </label>
                <UnitTypeSelect
                  value={priceUnit}
                  onChange={setPriceUnit}
                  ariaLabel={t.createProduct.priceUnitSearchPlaceholder}
                  invalid={priceUnitInvalid}
                />
              </div>
            </div>

            <div className="create-or-update-negotiable-checkbox-wrapper">
              <label className="create-or-update-checkbox-label">
                <input
                  type="checkbox"
                  className="create-or-update-checkbox-input"
                  checked={negotiable}
                  onChange={(e) => setNegotiable(e.target.checked)}
                />
                <span className="create-or-update-checkbox-custom">
                  {negotiable && (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14.7728 7.33886L9.6972 14.0055C9.48831 14.28 9.16497 14.4422 8.82053 14.4444H8.81275C8.47164 14.4444 8.14942 14.2866 7.93831 14.0178L5.23609 10.5655C4.85831 10.0833 4.94275 9.38442 5.42609 9.00664C5.90942 8.62775 6.6072 8.7122 6.98609 9.19664L8.80053 11.5144L13.005 5.99331C13.3761 5.50553 14.0739 5.40997 14.5628 5.7822C15.0505 6.15442 15.145 6.85109 14.7728 7.33886Z" fill="#071105"/>
                    </svg>
                  )}
                </span>
                {t.createProduct.negotiable}
              </label>
            </div>

            <div className="create-or-update-category-field-group">
              <label className="create-or-update-field-label">
                {t.createProduct.categoryLabel}
                <span className="create-or-update-mandatory-indicator">*</span>
              </label>
              <div className="create-or-update-category-dropdown-wrapper">
                <input
                  type="text"
                  className={`create-or-update-text-input create-or-update-category-dropdown-input ${categoryInvalid ? 'create-or-update-field-invalid' : ''}`}
                  placeholder={t.createProduct.categoryPlaceholder}
                  value={showCategoryDropdown ? categorySearch : category}
                  onChange={(e) => {
                    setCategorySearch(e.target.value);
                  }}
                  onFocus={handleCategoryFocus}
                  onBlur={() => {
                    setTimeout(() => {
                      setShowCategoryDropdown(false);
                      setCategorySearch('');
                    }, 200);
                  }}
                  required
                  aria-invalid={categoryInvalid}
                />
                <svg className={`create-or-update-category-chevron themed-dropdown-chevron ${showCategoryDropdown ? 'themed-dropdown-chevron-open' : ''}`} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.9999 16C11.7719 16 11.5449 15.923 11.3599 15.768L5.35991 10.768C4.93591 10.415 4.87791 9.78399 5.23191 9.35999C5.58491 8.93599 6.21491 8.87899 6.63991 9.23199L12.0109 13.708L17.3729 9.39299C17.8029 9.04699 18.4329 9.11499 18.7789 9.54499C19.1249 9.97499 19.0569 10.604 18.6269 10.951L12.6269 15.779C12.4439 15.926 12.2219 16 11.9999 16Z" fill="#071105"/>
                </svg>
                {showCategoryDropdown && categoryOptions.length > 0 && (
                  <div className="create-or-update-category-dropdown-options themed-dropdown-menu">
                    {categoryOptions.map((cat) => (
                      <div
                        key={cat.id}
                        className={`create-or-update-category-option themed-dropdown-option ${selectedCategoryId === cat.id ? 'themed-dropdown-option-active' : ''}`}
                        onClick={() => handleCategorySelect(cat)}
                      >
                        {cat.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="create-or-update-subcategory-field-group">
              <div className="create-or-update-field-label-row">
                <label className="create-or-update-field-label">{t.createProduct.subcategoryLabel}</label>
                <span className="create-or-update-optional-label">{t.createProduct.subcategoryOptional}</span>
              </div>
              <div className="create-or-update-subcategory-dropdown-wrapper">
                <input
                  type="text"
                  className={`create-or-update-text-input create-or-update-subcategory-dropdown-input ${!selectedCategoryId ? 'create-or-update-disabled' : ''}`}
                  placeholder={t.createProduct.subcategoryPlaceholder}
                  value={showSubcategoryDropdown ? subcategorySearch : subcategory}
                  onChange={(e) => {
                    if (selectedCategoryId) {
                      setSubcategorySearch(e.target.value);
                    }
                  }}
                  onFocus={() => {
                    if (selectedCategoryId) {
                      setSubcategorySearch('');
                      setShowSubcategoryDropdown(true);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setShowSubcategoryDropdown(false);
                      setSubcategorySearch('');
                    }, 200);
                  }}
                  disabled={!selectedCategoryId}
                />
                <svg className={`create-or-update-subcategory-chevron themed-dropdown-chevron ${showSubcategoryDropdown ? 'themed-dropdown-chevron-open' : ''}`} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.9999 16C11.7719 16 11.5449 15.923 11.3599 15.768L5.35991 10.768C4.93591 10.415 4.87791 9.78399 5.23191 9.35999C5.58491 8.93599 6.21491 8.87899 6.63991 9.23199L12.0109 13.708L17.3729 9.39299C17.8029 9.04699 18.4329 9.11499 18.7789 9.54499C19.1249 9.97499 19.0569 10.604 18.6269 10.951L12.6269 15.779C12.4439 15.926 12.2219 16 11.9999 16Z" fill="#071105"/>
                </svg>
                {showSubcategoryDropdown && subcategoryOptions.length > 0 && (
                  <div className="create-or-update-subcategory-dropdown-options themed-dropdown-menu">
                    <div
                      className={`create-or-update-subcategory-option themed-dropdown-option ${!selectedSubcategoryId ? 'themed-dropdown-option-active' : ''}`}
                      onClick={() => {
                        setSubcategory('');
                        setSelectedSubcategoryId('');
                        setShowSubcategoryDropdown(false);
                        setSubcategorySearch('');
                      }}
                    >
                      <em style={{ color: '#999' }}>Nicio subcategorie</em>
                    </div>
                    {subcategoryOptions.map((subcat) => (
                      <div
                        key={subcat.id}
                        className={`create-or-update-subcategory-option themed-dropdown-option ${selectedSubcategoryId === subcat.id ? 'themed-dropdown-option-active' : ''}`}
                        onClick={() => handleSubcategorySelect(subcat)}
                      >
                        {subcat.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <div className="create-or-update-actions-footer">
          <div className="create-or-update-actions-buttons">
            <button
              type="button"
              className="create-or-update-cancel-button"
              onClick={handleCancel}
              disabled={isPublishing}
            >
              {t.common.cancel}
            </button>
            <button className="create-or-update-preview-button" onClick={handlePreview} disabled={isPublishing || imagesProcessing}>
              {t.createProduct.previewButton}
            </button>
            <button className="create-or-update-publish-button" onClick={handlePublish} disabled={isPublishing || imagesProcessing || !hasChanges}>
              {isPublishing
                ? t.editProduct.saving
                : imagesProcessing ? t.createProduct.processingImages : t.editProduct.saveButton}
            </button>
          </div>
        </div>
        {isPublishing && (
          <div className="create-or-update-saving-overlay">
            <div className="create-or-update-saving-card">
              <LoadingIndicator message={t.editProduct.savingOverlay} />
              {images.some(image => !image.isExisting) && <p>{t.createProduct.savingOverlayHint}</p>}
            </div>
          </div>
        )}
      </main>
      <Footer
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookies={onNavigateToCookies}
        onNavigateToAbout={onNavigateToAbout}
        onNavigateToContact={onNavigateToContact}
      />
    </div>
    </>
  );
};

export default EditProduct;
